import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { ArrowLeft, ClipboardList, Loader2, MapPin, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";
import { LoginPrompt } from "@/components/auth/AuthDialog";
import HelioLogoMark from "@/components/HelioLogoMark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCreateInstallerSurvey, useInstallerSurveyList } from "@/api/hooks";
import { ApiError } from "@/api/client";
import type { InstallerSurveyListItem } from "@/api/types";
import { openDashboardPath } from "@/contexts/AuthContext";
import { formatRelativeTime } from "@/utils/date";

function statusLabel(item: InstallerSurveyListItem): string {
  if (item.listStatus === "attached") return "Attached";
  if (item.listStatus === "submitted") return "Submitted";
  return "Draft";
}

export default function InstallerSurveyList() {
  const [, navigate] = useLocation();
  const { loading, isAuthenticated, user } = useAuth();
  const { data, isLoading, error } = useInstallerSurveyList(isAuthenticated);
  const createSurvey = useCreateInstallerSurvey();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState<"last_saved_at" | "property" | "status">("last_saved_at");

  const rows = useMemo(() => {
    const list = Array.isArray(data) ? [...data] : [];
    const filtered = list.filter((item) => {
      if (status !== "all" && item.listStatus !== status) return false;
      if (!search.trim()) return true;
      const hay = `${item.propertyAddress ?? ""} ${item.propertyPostcode ?? ""}`.toLowerCase();
      return hay.includes(search.trim().toLowerCase());
    });
    filtered.sort((a, b) => {
      if (sort === "status") return a.listStatus.localeCompare(b.listStatus);
      if (sort === "property") {
        return (a.propertyAddress ?? "").localeCompare(b.propertyAddress ?? "");
      }
      return (b.lastSavedAt ?? "").localeCompare(a.lastSavedAt ?? "");
    });
    return filtered;
  }, [data, search, sort, status]);

  if (loading || (isAuthenticated && isLoading)) {
    return (
      <div className="min-h-screen bg-[#050d1a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#29ffa9] animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050d1a] text-white">
        <LoginPrompt message="Sign in to record a field survey." />
      </div>
    );
  }

  const goBack = () => {
    if (user?.role === "admin") {
      void openDashboardPath("/admin/field-surveys");
      return;
    }
    navigate("/installer");
  };

  const startSurvey = async () => {
    try {
      const survey = await createSurvey.mutateAsync();
      navigate(`/installer/surveys/${survey.id}`);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Could not start a survey.";
      toast.error(message);
    }
  };

  return (
    <div className="min-h-screen bg-[#050d1a] text-white">
      <header className="sticky top-0 z-20 border-b border-white/8 bg-[#050d1a]/95 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-3 px-4 py-4">
          <div className="flex items-center gap-3">
            <button type="button" className="text-white/60 hover:text-white" onClick={goBack} aria-label="Back">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <HelioLogoMark className="text-base" />
            <span className="text-sm font-semibold">Field surveys</span>
          </div>
          <Button className="btn-cta" disabled={createSurvey.isPending} onClick={() => void startSurvey()}>
            {createSurvey.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Start a survey
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-6 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search address"
            className="bg-[#132035] border-white/10 text-white"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-md border border-white/10 bg-[#132035] px-3 py-2 text-sm"
          >
            <option value="all">All statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="attached">Attached</option>
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as typeof sort)}
            className="rounded-md border border-white/10 bg-[#132035] px-3 py-2 text-sm"
          >
            <option value="last_saved_at">Last saved</option>
            <option value="property">Property</option>
            <option value="status">Status</option>
          </select>
        </div>

        {error ? (
          <p className="text-sm text-red-400">Could not load surveys.</p>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-white/8 bg-[#0d1b2e] py-16 text-center">
            <ClipboardList className="h-10 w-10 text-white/20 mx-auto mb-3" />
            <p className="text-white/40">No field surveys yet.</p>
          </div>
        ) : (
          <div className="rounded-xl border border-white/8 bg-[#0d1b2e] divide-y divide-white/8">
            {rows.map((item) => (
              <button
                key={item.id}
                type="button"
                className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-white/5"
                onClick={() => navigate(`/installer/surveys/${item.id}`)}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#29ffa9]/10">
                  <MapPin className="h-5 w-5 text-[#29ffa9]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{item.propertyAddress || "Address not set"}</p>
                  <p className="text-xs text-white/40">
                    {item.propertyPostcode || "No postcode"}
                    {item.lastSavedAt ? ` · ${formatRelativeTime(item.lastSavedAt)}` : ""}
                  </p>
                </div>
                <span className="text-xs uppercase tracking-wide text-white/50">{statusLabel(item)}</span>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
